import { useEffect, useState, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { isEqual } from "lodash";
import "../../AppPage.css";
import {
  ADAPT_METHOD_OPTIONS,
  FRAME_RATE_LIST,
  WAVE_PROPAGATION_SPEED,
} from "../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import GlimpseBreadCrumbs from "../../../components/glimpse/GlimpseBreadCrumbs";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { NumericInput } from "../../../components/NumericInput";
import { CancelTitleBarMenu } from "../../../components/titleBarMenu/CancelTitleBarMenu";
import { PropertySubheader } from "../../../components/PropertySubheader";
import { PercentageInput } from "../../../components/PercentageInput";
import { CustomSwitch } from "../../../components/CustomSwitch";
import { loadClip } from "../utils/dataLoader";
import {
  getNavigationUrl,
  checkStatusWhitelist,
} from "../../../store/navigationUtil";
import { useGlimpse } from "../../../store/glimpse/hooks";
import { useModals } from "../../../store/dialogs/hooks.js";
import { GlimpseModelLengthDialog } from "../../../components/dialogs/GlimpseModelLengthDialog.js";
import FrameSizes from "../../../components/glimpse/FrameSizes";
import {
  computeFrameSizeList,
  findNearestFrameSize,
  formatFrameSizeMs,
} from "../../../components/glimpse/frameSizeListUtil";

const maxWidth = 300;
// minHeight allows consistent space for helper text
const minHeight = 160;

export default function GlimpsePropertiesPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [clip, setClip] = useState({});
  const [started, setStarted] = useState(false);
  const [initialState, setInitialState] = useState({});
  const { openGlimpseModelLengthDialog } = useModals();

  const {
    adaptMethod,
    modelAudioDuration,
    modelCaseId,
    modelClipId,
    frameSizeLow,
    frameSizeHi,
    useFrameSizeList,
    frameSizeListMin,
    frameSizeListMax,
    frameSizeListSteps,
    speedOfSoundInCm,
    gateLevelGF,
    midModelLengthGF,
    modelClipSnapshot,
    utteranceLength,
    gateLevelD,
    midModelLengthD,
    lowPass,
    highPass,
    arrayWidth,
    isValid,
    isFieldValid,
    getFieldErrors,
    queueRequest,
    update: glimpseUpdate,
    reset: glimpseReset,
    resetParameters,
    getProcessingParameters,
  } = useGlimpse();

  const { caseId, clipId } = useParams();
  const { minLength } = modelClipSnapshot || {};

  const load = useCallback(async () => {
    const currClip = await loadClip(caseId, clipId);
    if (!currClip) {
      navigate(getNavigationUrl(caseId));
    }
    if (!checkStatusWhitelist(currClip, "EDITING")) {
      navigate(getNavigationUrl(caseId, currClip));
    }
    setClip(currClip);
    if (!modelCaseId || !modelClipId) {
      navigate(`/app/cases/${caseId}/clips/${clipId}/glimpse/selectFiles`);
    }
    if (currClip.arrayWidth)
      glimpseUpdate({ arrayWidth: currClip.arrayWidth.toString() });
  }, [caseId, clipId, navigate, modelCaseId, modelClipId, glimpseUpdate]);

  const handleQueueGlimpse = async () => {
    setStarted(true);
    await queueRequest(
      modelCaseId,
      modelClipId,
      caseId,
      clipId,
      getProcessingParameters(),
    );
    const url = `/app/cases/${caseId}/clips/${clipId}`;
    setStarted(false);
    navigate(url);
  };

  const effectiveFrameSizeHi = useFrameSizeList
    ? frameSizeListMax
    : frameSizeHi;

  const handleRun = () => {
    if (effectiveFrameSizeHi > minLength) {
      openGlimpseModelLengthDialog();
      return;
    }
    handleQueueGlimpse();
  };

  useEffect(() => {
    load();
  }, [load]);

  const isUpdated = isEqual(
    initialState,
    Object.entries({
      adaptMethod,
      frameSizeLow,
      frameSizeHi,
      useFrameSizeList,
      frameSizeListMin,
      frameSizeListMax,
      frameSizeListSteps,
      speedOfSoundInCm,
      gateLevelGF,
      midModelLengthGF,
      utteranceLength,
      gateLevelD,
      midModelLengthD,
      lowPass,
      highPass,
      arrayWidth,
    }).reduce((acc, [key, value]) => {
      acc[key] = Number(value);
      return acc;
    }, {}),
  );

  useEffect(() => {
    setInitialState(
      Object.entries({
        adaptMethod,
        frameSizeLow,
        frameSizeHi,
        useFrameSizeList,
        frameSizeListMin,
        frameSizeListMax,
        frameSizeListSteps,
        speedOfSoundInCm,
        gateLevelGF,
        midModelLengthGF,
        utteranceLength,
        gateLevelD,
        midModelLengthD,
        lowPass,
        highPass,
        arrayWidth,
      }).reduce((acc, [key, value]) => {
        acc[key] = Number(value);
        return acc;
      }, {}),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const frameSizeListMinIndex = FrameSizes.indexOf(
    findNearestFrameSize(frameSizeListMin),
  );
  const frameSizeListMaxIndex = FrameSizes.indexOf(
    findNearestFrameSize(frameSizeListMax),
  );
  const previewFrameSizeList = computeFrameSizeList(
    frameSizeListMin,
    frameSizeListMax,
    frameSizeListSteps,
  );

  // State for the Wave Propagation Speed component to manage the selected value
  const [wpsValue, setWPSValue] = useState(
    WAVE_PROPAGATION_SPEED.find(
      (option) => option.value === speedOfSoundInCm,
    ) || null,
  );

  useEffect(() => {
    setWPSValue((currentValue) => {
      if (
        currentValue &&
        typeof currentValue === "object" &&
        currentValue.value === speedOfSoundInCm
      ) {
        return currentValue;
      }

      return (
        WAVE_PROPAGATION_SPEED.find(
          (option) => option.value === speedOfSoundInCm,
        ) || (speedOfSoundInCm == null ? null : String(speedOfSoundInCm))
      );
    });
  }, [speedOfSoundInCm]);

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} p={2}>
        <GlimpseBreadCrumbs active={2} />
        <Typography variant="h6" style={{ marginTop: 16 }}>
          Selected Parameters
        </Typography>
        <PropertySubheader value="ADAPTIVE FILTER PROPERTIES" />
        <Box display="flex" mb={3} p={1}>
          <FormControlLabel
            control={
              <CustomSwitch
                checked={useFrameSizeList}
                onChange={(event) =>
                  glimpseUpdate({ useFrameSizeList: event.target.checked })
                }
              />
            }
            label="Use Frame Size List"
          />
        </Box>
        <Box
          display="flex"
          columnGap={2}
          p={1}
          rowGap={4}
          flexWrap="wrap"
          mb={2}
          width="fit-content"
          sx={{
            p: 3,
            backgroundColor: useFrameSizeList ? "action.selected" : "inherit",
            borderRadius: 2,
            border: "1px solid",
            borderColor: useFrameSizeList
              ? "transparent"
              : theme.palette.primary.main,
            transition: "all 0.2s ease-in-out",
          }}
        >
          <FormControl
            error={!isFieldValid("frameSizeLow")}
            fullWidth
            sx={{
              flex: 1,
              maxWidth: { sm: maxWidth },
              minHeight: { sm: minHeight },
              minWidth: maxWidth,
            }}
          >
            <InputLabel htmlFor="frame-size-low" id="frame-size-low-label">
              Frame Size (Focusing Aperture) Low
            </InputLabel>
            <Select
              disabled={useFrameSizeList}
              inputProps={{ id: "frame-size-low" }}
              labelId="frame-size-low-label"
              label="Frame Size (Focusing Aperture) Low"
              value={frameSizeLow}
              onChange={(event) =>
                glimpseUpdate({ frameSizeLow: Number(event.target.value) })
              }
            >
              {FRAME_RATE_LIST.map((data) => {
                return (
                  <MenuItem key={data.value} value={data.value}>
                    {data.label}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText>
              {isFieldValid("frameSizeLow")
                ? "Minimum processing frame size in samples."
                : getFieldErrors("frameSizeLow").join(", ")}
            </FormHelperText>
          </FormControl>
          <FormControl
            error={!isFieldValid("frameSizeHi")}
            fullWidth
            sx={{
              flex: 1,
              maxWidth: { sm: maxWidth },
              minHeight: { sm: minHeight },
              minWidth: maxWidth,
            }}
          >
            <InputLabel htmlFor="frame-size-hi" id="frame-size-hi-label">
              Frame Size (Focusing Aperture) High
            </InputLabel>
            <Select
              disabled={useFrameSizeList}
              inputProps={{ id: "frame-size-hi" }}
              labelId="frame-size-hi-label"
              label="Frame Size (Focusing Aperture) High"
              value={frameSizeHi}
              onChange={(event) =>
                glimpseUpdate({ frameSizeHi: Number(event.target.value) })
              }
            >
              {FRAME_RATE_LIST.map((data) => {
                return (
                  <MenuItem key={data.value} value={data.value}>
                    {data.label}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText>
              {isFieldValid("frameSizeHi")
                ? "Maximum processing frame size in samples. Set equal\nto minimum frame size if only the single size is desired."
                : getFieldErrors("frameSizeHi").join(", ")}
            </FormHelperText>
          </FormControl>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          rowGap={3}
          flexDirection="column"
          mb={3}
          width="fit-content"
          sx={{
            p: 3,
            backgroundColor: !useFrameSizeList ? "action.selected" : "inherit",
            borderRadius: 2,
            border: "1px solid",
            borderColor: !useFrameSizeList
              ? "transparent"
              : theme.palette.primary.main,
            minWidth: { md: 666 },
            transition: "all 0.2s ease-in-out",
          }}
        >
          <Box width="100%" maxWidth={maxWidth * 2 + 16}>
            <Typography gutterBottom>
              Frame Size (Focusing Aperture) Range
            </Typography>
            <Slider
              disabled={!useFrameSizeList}
              value={[frameSizeListMinIndex, frameSizeListMaxIndex]}
              onChange={(event, newValue) =>
                glimpseUpdate({
                  frameSizeListMin: FrameSizes[newValue[0]],
                  frameSizeListMax: FrameSizes[newValue[1]],
                })
              }
              min={0}
              max={FrameSizes.length - 1}
              disableSwap
              valueLabelDisplay="auto"
              valueLabelFormat={(index) =>
                `${FrameSizes[index]}(${formatFrameSizeMs(FrameSizes[index], clip.sampleRate)})`
              }
            />
            <FormHelperText>
              {`${FrameSizes[frameSizeListMinIndex]}(${formatFrameSizeMs(FrameSizes[frameSizeListMinIndex], clip.sampleRate)})`}{" "}
              –{" "}
              {`${FrameSizes[frameSizeListMaxIndex]}(${formatFrameSizeMs(FrameSizes[frameSizeListMaxIndex], clip.sampleRate)})`}
            </FormHelperText>
          </Box>
          <Box width="100%" maxWidth={maxWidth * 2 + 16} minHeight={140}>
            <Typography gutterBottom>Number of Steps</Typography>
            <Slider
              disabled={!useFrameSizeList}
              value={frameSizeListSteps}
              onChange={(event, newValue) =>
                glimpseUpdate({ frameSizeListSteps: newValue })
              }
              min={2}
              max={10}
              step={1}
              marks
              valueLabelDisplay="auto"
            />
            <FormHelperText>
              {previewFrameSizeList.length} frame size
              {previewFrameSizeList.length === 1 ? "" : "s"}:{" "}
              {previewFrameSizeList
                .map(
                  (size) =>
                    `${size}(${formatFrameSizeMs(size, clip.sampleRate)})`,
                )
                .join(", ")}
            </FormHelperText>
          </Box>
        </Box>
        <Box display="flex" columnGap={2} p={1} rowGap={4} flexWrap="wrap">
          <FormControl fullWidth sx={{ maxWidth: { sm: maxWidth } }}>
            <InputLabel htmlFor="adapt-method" id="adapt-method-label">
              Adapt Method
            </InputLabel>
            <Select
              inputProps={{ id: "adapt-method" }}
              labelId="adapt-method-label"
              label="Adapt Mehod"
              value={adaptMethod}
              onChange={(event) =>
                glimpseUpdate({ adaptMethod: Number(event.target.value) })
              }
            >
              {ADAPT_METHOD_OPTIONS.map((data) => {
                return (
                  <MenuItem key={data.value} value={data.value}>
                    <Tooltip title={data.tooltipText} placement="left" arrow>
                      <Typography sx={{ width: "100%" }}>
                        {data.label}
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
        <Accordion
          sx={{
            border: "none",
            backgroundColor: theme.palette.background.default,
            boxShadow: "none",
            mt: 4,
            pb: 12,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <PropertySubheader value="Advanced" />
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" columnGap={2} p={1} rowGap={4} flexWrap="wrap">
              <NumericInput
                label="Gate Level GF"
                value={gateLevelGF}
                valid={isFieldValid("gateLevelGF")}
                errorText={getFieldErrors("gateLevelGF").join(", ")}
                caption="Controls the threshold under which softer sounds are excluded from the desired source location’s spatial model. Higher settings exclude more soft sounds."
                onChanged={(value) =>
                  glimpseUpdate({ gateLevelGF: Number(value) })
                }
                right="%"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <PercentageInput
                decimal={2}
                pctDecimal={0}
                label="Minimum Model Length GF"
                value={midModelLengthGF}
                valid={isFieldValid("midModelLengthGF")}
                errorText={getFieldErrors("midModelLengthGF").join(", ")}
                baseNumber={modelAudioDuration}
                caption="Process this percent of modeling data."
                onChanged={(value) =>
                  glimpseUpdate({ midModelLengthGF: Number(value) })
                }
                right="%"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <Autocomplete
                freeSolo
                fullWidth
                options={WAVE_PROPAGATION_SPEED}
                value={wpsValue}
                getOptionLabel={(option) => {
                  if (typeof option === "object") return option?.label ?? "";
                  return String(option ?? "");
                }}
                isOptionEqualToValue={(option, val) => {
                  if (!val) return false;
                  if (typeof val === "object") return option === val;
                  return option.label === val;
                }}
                onChange={(_, newValue) => {
                  setWPSValue(newValue);
                  const speed =
                    typeof newValue === "object"
                      ? Number(newValue?.value)
                      : Number(newValue);
                  glimpseUpdate({
                    speedOfSoundInCm: Number.isFinite(speed) ? speed : null,
                  });
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (!newInputValue.trim()) {
                    setWPSValue(null);
                    glimpseUpdate({ speedOfSoundInCm: null });
                    return;
                  }
                  if (reason === "selectOption" || reason === "reset") {
                    return;
                  }
                  if (
                    typeof wpsValue === "object" &&
                    wpsValue?.label === newInputValue
                  ) {
                    return;
                  }

                  setWPSValue(newInputValue);
                  const speed = Number(newInputValue);
                  if (Number.isFinite(speed)) {
                    glimpseUpdate({ speedOfSoundInCm: speed });
                  }
                }}
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Wave Propagation Speed"
                    variant="outlined"
                    error={getFieldErrors("speedOfSoundInCm").length > 0}
                    helperText={
                      isFieldValid("speedOfSoundInCm")
                        ? "Speed of sound in current medium. Select from list or enter a value in cm/s."
                        : getFieldErrors("speedOfSoundInCm").join(", ")
                    }
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            <InputAdornment position="end">cm/s</InputAdornment>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box display="flex">
              <PropertySubheader value="DELAY ESTIMATOR PARAMETERS" />
            </Box>
            <Box display="flex" columnGap={2} p={1} rowGap={4} flexWrap="wrap">
              <NumericInput
                label="Utterance Length"
                value={utteranceLength}
                valid={isFieldValid("utteranceLength")}
                errorText={getFieldErrors("utteranceLength").join(", ")}
                caption="Minimum expected length in ms of useful modeling sound."
                onChanged={(value) =>
                  glimpseUpdate({ utteranceLength: Number(value) })
                }
                right="ms"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <NumericInput
                label="Gate Level D"
                value={gateLevelD}
                valid={isFieldValid("gateLevelD")}
                errorText={getFieldErrors("gateLevelD").join(", ")}
                caption="Controls the threshold under which softer sounds are excluded from the desired source location delay estimator. Higher settings exclude more soft sounds."
                onChanged={(value) =>
                  glimpseUpdate({ gateLevelD: Number(value) })
                }
                right="%"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <PercentageInput
                decimal={2}
                pctDecimal={0}
                baseNumber={modelAudioDuration}
                label="Minimum Model Length D"
                value={midModelLengthD}
                valid={isFieldValid("midModelLengthD")}
                errorText={getFieldErrors("midModelLengthD").join(", ")}
                caption="Process this percent of modeling data."
                onChanged={(value) =>
                  glimpseUpdate({ midModelLengthD: Number(value) })
                }
                right="%"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
            </Box>
            <Box display="flex" columnGap={2} p={1} rowGap={4} flexWrap="wrap">
              <NumericInput
                label="High Pass"
                value={highPass}
                valid={isFieldValid("highPass")}
                errorText={getFieldErrors("highPass").join(", ")}
                caption="Attenuate below this frequency (in Hz) for delay estimator."
                onChanged={(value) =>
                  glimpseUpdate({ highPass: Number(value) })
                }
                right="Hz"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <NumericInput
                label="Low Pass"
                value={lowPass}
                valid={isFieldValid("lowPass")}
                errorText={getFieldErrors("lowPass").join(", ")}
                caption="Attenuate above this frequency (in Hz) for delay estimator."
                onChanged={(value) => glimpseUpdate({ lowPass: Number(value) })}
                right="Hz"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight },
                }}
              />
              <NumericInput
                label="Array Width"
                value={arrayWidth}
                valid={isFieldValid("arrayWidth")}
                errorText={getFieldErrors("arrayWidth").join(", ")}
                caption="Controls maximum search space. For a planar array, this can be half the actual array width if the desired source location is in the far field and < 30° off-axis. For a solid spherical array, multiply by π/2  if the desired source location is in the near field."
                onChanged={(value) =>
                  glimpseUpdate({ arrayWidth: Number(value) })
                }
                right="cm"
                sx={{
                  maxWidth: { sm: maxWidth },
                  minHeight: { sm: minHeight + 20 },
                }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          position: "fixed",
          transform: "translateX(-50%)",
          bottom: 64,
          mb: 1,
          left: "50%",
          zIndex: 1,
          width: "calc(100% - 1rem)",
        }}
      >
        <Button
          variant="outlined"
          onClick={async () => {
            await glimpseReset();
            navigate(`/app/cases/${caseId}/clips/${clipId}/editor`);
          }}
        >
          CANCEL
        </Button>
        <Box display="flex" gap={2}>
          <Button
            disabled={isUpdated}
            variant="outlined"
            onClick={async () => {
              await resetParameters();
            }}
          >
            RESET
          </Button>
          <Button
            variant="contained"
            endIcon={<ChevronRight />}
            disabled={!isValid || started}
            onClick={handleRun}
          >
            {started ? "RUNNING" : "RUN GLIMPSE"}
          </Button>
        </Box>
      </Paper>
      <Box position="fixed" bottom={0} width={1} zIndex={1}>
        <ContentTitleBar
          menu={<CancelTitleBarMenu clipId={clipId} caseId={caseId} />}
          name={clip?.description}
          label={clip?.description ? "Create GLIMPSE Job" : ""}
          onBack={() =>
            navigate(`/app/cases/${caseId}/clips/${clipId}/glimpse/selectFiles`)
          }
        />
      </Box>
      <GlimpseModelLengthDialog
        frameSizeHi={effectiveFrameSizeHi}
        minLength={minLength}
        handleQueueGlimpse={handleQueueGlimpse}
      />
    </Stack>
  );
}
